import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../contexts/ThemeContext';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlus, Users, Building2, Plus, Edit2, Trash2, X, Check,
  Clock, DollarSign, Activity, TrendingUp, Shield, Crown, Medal,
  ChevronRight, Eye, History, Calendar, Zap, Target, RefreshCw,
  Star, Flame, CheckCircle, XCircle, Timer
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatRelativeTime } from '../lib/utils';

// Animated background particles
function GamingParticles({ theme }) {
  if (theme === 'light') return null;
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: ['#FF6B00', '#9945FF', '#00F5FF', '#FFD700'][i % 4],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.5, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

// Performance tier badge
function TierBadge({ tier, theme }) {
  const config = {
    HIGH: { icon: Crown, color: '#00FF88', label: 'ELITE' },
    MEDIUM: { icon: Shield, color: '#FFD700', label: 'WARRIOR' },
    LOW: { icon: Target, color: '#FF3366', label: 'ROOKIE' }
  };
  const { icon: Icon, color, label } = config[tier] || config.MEDIUM;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold"
      style={{ 
        backgroundColor: theme === 'dark' ? `${color}20` : `${color}15`, 
        color,
        border: `1px solid ${color}40`
      }}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

export default function GuildMembersPage() {
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeIntel, setEmployeeIntel] = useState([]);
  const [branchPages, setBranchPages] = useState([]);
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [approving, setApproving] = useState(null);
  
  // Selected member for detail view
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Employee form
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({ username: '', email: '', password: '', assigned_page_id: '' });

  // Theme-aware colors
  const colors = {
    bg: theme === 'dark' ? '#080810' : '#f8fafc',
    card: theme === 'dark' ? '#0d0d15' : '#ffffff',
    cardHover: theme === 'dark' ? '#14141f' : '#f1f5f9',
    text: theme === 'dark' ? '#ffffff' : '#1e293b',
    textMuted: theme === 'dark' ? '#9ca3af' : '#64748b',
    border: theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
    accent: theme === 'dark' ? '#FF6B00' : '#ea580c',
    accentBg: theme === 'dark' ? 'rgba(255,107,0,0.1)' : 'rgba(234,88,12,0.1)',
  };

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      const [empRes, pagesRes, intelRes] = await Promise.all([
        axios.get('/users?role=employee'),
        axios.get('/branch-pages'),
        axios.get('/employee-intel')
      ]);
      setEmployees(empRes.data);
      setBranchPages(pagesRes.data);
      setEmployeeIntel(intelRes.data.employees || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMemberDetail = async (employeeId) => {
    try {
      setLoadingDetail(true);
      const response = await axios.get(`/employee-intel/${employeeId}`);
      setMemberDetail(response.data);
    } catch (error) {
      toast.error('Failed to load member details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectMember = (emp) => {
    setSelectedMember(emp);
    fetchMemberDetail(emp.employee_id || emp.id);
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/employees', { ...employeeForm, role: 'employee' });
      toast.success('Guild member recruited!');
      setEmployeeForm({ username: '', email: '', password: '', assigned_page_id: '' });
      setShowEmployeeForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recruit member');
    }
  };

  // Edit employee state
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', is_active: true, assigned_page_id: '', password: '' });
  const [editShiftStatus, setEditShiftStatus] = useState(null); // 'active' or null
  const [shiftLoading, setShiftLoading] = useState(false);

  const handleEditEmployee = async (emp) => {
    setEditingEmployee(emp);
    setEditForm({ 
      username: emp.username, 
      email: emp.email, 
      is_active: emp.is_active !== false,
      assigned_page_id: emp.assigned_page_id || '',
      password: ''
    });
    // Check if employee has an active shift
    try {
      const res = await axios.get(`/shifts?employee_id=${emp.id}`);
      const active = res.data?.find(s => s.status === 'active');
      setEditShiftStatus(active ? 'active' : null);
    } catch { setEditShiftStatus(null); }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await axios.put(`/employees/${editingEmployee.id}`, payload);
      toast.success('Member updated!');
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleAdminShift = async (action) => {
    if (!editingEmployee) return;
    setShiftLoading(true);
    try {
      const res = await axios.post(`/admin/shifts/${editingEmployee.id}`, { action });
      toast.success(res.data.message);
      setEditShiftStatus(action === 'in' ? 'active' : null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Shift action failed');
    }
    setShiftLoading(false);
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDeleteEmployee = async (empId) => {
    setDeleteConfirm(empId);
  };

  const confirmDeleteEmployee = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`/employees/${deleteConfirm}`);
      toast.success('Member removed from guild');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleApproveEmployee = async (empId) => {
    setApproving(empId);
    try {
      await axios.patch(`/employees/${empId}/approve`);
      toast.success('Member approved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve member');
    } finally {
      setApproving(null);
    }
  };

  const handleRejectEmployee = async (empId) => {
    setApproving(empId);
    try {
      await axios.patch(`/employees/${empId}/reject`);
      toast.success('Member rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject member');
    } finally {
      setApproving(null);
    }
  };

  const activeEmployees = employees.filter(e => e.status !== 'pending' && e.status !== 'rejected');
  const pendingEmployees = employees.filter(e => e.status === 'pending');

  const handleAssignPage = async (userId, pageId) => {
    try {
      await axios.put(`/users/${userId}/assign-page`, { page_id: pageId });
      toast.success('Arena assigned!');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign arena');
    }
  };

  const getPageById = (pageId) => branchPages.find(p => p.id === pageId);
  
  const getEmployeeIntel = (empId) => employeeIntel.find(e => e.employee_id === empId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.bg }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="relative">
          <div className="w-20 h-20 rounded-full border-4" style={{ borderColor: `${colors.accent}30`, borderTopColor: colors.accent }} />
          <Users className="absolute inset-0 m-auto w-8 h-8" style={{ color: colors.accent }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: colors.bg }} data-testid="guild-members-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background Effects */}
      <GamingParticles theme={theme} />
      {theme === 'dark' && (
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#FF6B00]/5 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#9945FF]/5 rounded-full blur-[150px]" />
        </div>
      )}

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-4">
            <motion.div
              animate={theme === 'dark' ? { 
                boxShadow: ['0 0 20px #FF6B00', '0 0 40px #FF6B00', '0 0 20px #FF6B00'],
              } : {}}
              transition={{ repeat: Infinity, duration: 3 }}
              className="p-3 rounded-xl border"
              style={{ 
                background: theme === 'dark' 
                  ? 'linear-gradient(135deg, rgba(255,107,0,0.2), rgba(153,69,255,0.2))' 
                  : 'linear-gradient(135deg, rgba(234,88,12,0.1), rgba(139,92,246,0.1))',
                borderColor: theme === 'dark' ? 'rgba(255,107,0,0.3)' : 'rgba(234,88,12,0.2)'
              }}
            >
              <Users className="w-8 h-8" style={{ color: colors.accent }} />
            </motion.div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black" style={{ 
                color: colors.text,
                textShadow: theme === 'dark' ? '0 0 30px rgba(255,107,0,0.3)' : 'none'
              }}>
                GUILD MEMBERS
              </h1>
              <p style={{ color: colors.textMuted }}>Manage your team and track performance</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowEmployeeForm(true)}
              className="px-4 py-2.5 rounded-xl font-bold flex items-center gap-2"
              style={{ 
                background: theme === 'dark' 
                  ? 'linear-gradient(135deg, #FF6B00, #FFD700)' 
                  : 'linear-gradient(135deg, #ea580c, #f59e0b)',
                color: theme === 'dark' ? '#000' : '#fff'
              }}
              data-testid="add-member-btn"
            >
              <UserPlus className="w-4 h-4" /> RECRUIT
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchData}
              disabled={refreshing}
              className="p-2.5 rounded-xl border"
              style={{ 
                background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
                borderColor: colors.border,
                color: colors.textMuted
              }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </motion.button>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Members List */}
          <div className="lg:col-span-2">
            <Card className="border overflow-hidden" style={{ background: colors.card, borderColor: colors.border }}>
              <div 
                className="absolute top-0 left-0 w-full h-[2px]"
                style={{ background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)` }}
              />
              
              <div className="p-4 border-b" style={{ borderColor: colors.border }}>
                <h3 className="font-bold flex items-center gap-2" style={{ color: colors.text }}>
                  <Shield className="w-5 h-5" style={{ color: colors.accent }} /> 
                  ACTIVE MEMBERS ({activeEmployees.length})
                  {pendingEmployees.length > 0 && (
                    <Badge className="ml-2 text-xs animate-pulse" style={{ background: '#FFD70030', color: '#FFD700', border: '1px solid #FFD70040' }}>
                      {pendingEmployees.length} PENDING
                    </Badge>
                  )}
                </h3>
              </div>

              <ScrollArea className="h-[600px]">
                <div className="p-4 space-y-3">
                  {/* Pending Employees Section */}
                  {pendingEmployees.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3 px-1">
                        <Clock className="w-4 h-4" style={{ color: '#FFD700' }} />
                        <span className="text-sm font-bold" style={{ color: '#FFD700' }}>PENDING APPROVAL</span>
                      </div>
                      {pendingEmployees.map((emp, idx) => (
                        <motion.div
                          key={emp.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="p-4 rounded-xl border mb-2"
                          style={{
                            background: theme === 'dark' ? 'rgba(255,215,0,0.05)' : 'rgba(255,215,0,0.05)',
                            borderColor: theme === 'dark' ? 'rgba(255,215,0,0.2)' : 'rgba(245,158,11,0.2)'
                          }}
                          data-testid={`pending-member-${emp.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <Avatar className="w-10 h-10 border-2" style={{ borderColor: '#FFD700' }}>
                              <AvatarFallback className="text-white font-bold" style={{ background: 'rgba(255,215,0,0.2)' }}>
                                {emp.username?.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold truncate" style={{ color: colors.text }}>{emp.username}</p>
                              <p className="text-sm truncate" style={{ color: colors.textMuted }}>{emp.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                disabled={approving === emp.id}
                                onClick={(e) => { e.stopPropagation(); handleApproveEmployee(emp.id); }}
                                className="h-8 px-3 text-xs font-bold"
                                style={{ background: '#00FF88', color: '#000' }}
                                data-testid={`approve-member-${emp.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> APPROVE
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={approving === emp.id}
                                onClick={(e) => { e.stopPropagation(); handleRejectEmployee(emp.id); }}
                                className="h-8 px-3 text-xs font-bold hover:bg-[#FF3366]/10"
                                style={{ color: '#FF3366' }}
                                data-testid={`reject-member-${emp.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" /> REJECT
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      <div className="border-b my-4" style={{ borderColor: colors.border }} />
                    </div>
                  )}

                  {/* Active Members */}
                  {activeEmployees.map((emp, idx) => {
                    const intel = getEmployeeIntel(emp.id);
                    const assignedPage = getPageById(emp.assigned_page_id);
                    const isSelected = selectedMember?.id === emp.id || selectedMember?.employee_id === emp.id;
                    
                    return (
                      <motion.div
                        key={emp.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        whileHover={{ x: 5 }}
                        onClick={() => handleSelectMember({ ...emp, ...intel })}
                        className="p-4 rounded-xl cursor-pointer transition-all border"
                        style={{
                          background: isSelected 
                            ? (theme === 'dark' ? 'linear-gradient(135deg, rgba(255,107,0,0.1), rgba(153,69,255,0.1))' : 'linear-gradient(135deg, rgba(234,88,12,0.05), rgba(139,92,246,0.05))')
                            : (theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff'),
                          borderColor: isSelected 
                            ? (theme === 'dark' ? 'rgba(255,107,0,0.3)' : 'rgba(234,88,12,0.3)') 
                            : colors.border
                        }}
                        data-testid={`member-${emp.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <Avatar className="w-12 h-12 border-2" style={{ borderColor: intel?.performance?.tier_color || colors.accent }}>
                              <AvatarFallback 
                                className="text-white font-bold"
                                style={{ 
                                  background: `linear-gradient(135deg, ${intel?.performance?.tier_color || colors.accent}40, transparent)` 
                                }}
                              >
                                {emp.username?.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            {intel?.shifts?.active_shift && (
                              <motion.span 
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2"
                                style={{ background: '#00FF88', borderColor: colors.card }}
                              />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold truncate" style={{ color: colors.text }}>{emp.username}</p>
                              {intel && <TierBadge tier={intel.performance?.tier} theme={theme} />}
                            </div>
                            <p className="text-sm truncate" style={{ color: colors.textMuted }}>{emp.email}</p>
                            {assignedPage && (
                              <Badge 
                                className="mt-1 text-xs"
                                style={{ 
                                  background: `${assignedPage.color}20`, 
                                  color: assignedPage.color,
                                  border: `1px solid ${assignedPage.color}40`
                                }}
                              >
                                {assignedPage.name}
                              </Badge>
                            )}
                          </div>

                          <div className="text-right">
                            {intel && (
                              <>
                                <p className="text-2xl font-black" style={{ color: intel.performance?.tier_color }}>
                                  {intel.performance?.score}
                                </p>
                                <p className="text-xs" style={{ color: colors.textMuted }}>SCORE</p>
                              </>
                            )}
                          </div>

                          {/* Edit/Delete Buttons */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-[#00F5FF]/10"
                              onClick={(e) => { e.stopPropagation(); handleEditEmployee(emp); }}
                              data-testid={`edit-member-${emp.id}`}
                            >
                              <Edit2 className="w-4 h-4 text-[#00F5FF]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-[#FF3366]/10"
                              onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(emp.id); }}
                              data-testid={`delete-member-${emp.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-[#FF3366]" />
                            </Button>
                          </div>

                          <ChevronRight className="w-5 h-5" style={{ color: colors.textMuted }} />
                        </div>

                        {/* Quick Stats */}
                        {intel && (
                          <div className="mt-3 pt-3 grid grid-cols-4 gap-2 text-center" style={{ borderTop: `1px solid ${colors.border}` }}>
                            <div>
                              <p className="text-xs" style={{ color: colors.textMuted }}>Battles</p>
                              <p className="font-bold" style={{ color: colors.text }}>{intel.transactions?.total_count || 0}</p>
                            </div>
                            <div>
                              <p className="text-xs" style={{ color: colors.textMuted }}>Deposits</p>
                              <p className="font-bold text-[#00FF88]">{formatCurrency(intel.transactions?.total_deposits || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs" style={{ color: colors.textMuted }}>Hours</p>
                              <p className="font-bold" style={{ color: colors.text }}>{intel.shifts?.total_hours?.toFixed(1) || 0}h</p>
                            </div>
                            <div>
                              <p className="text-xs" style={{ color: colors.textMuted }}>Response</p>
                              <p className="font-bold" style={{ color: colors.accent }}>{intel.activity?.response_rate || 0}%</p>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}

                  {employees.length === 0 && (
                    <div className="text-center py-12" style={{ color: colors.textMuted }}>
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="font-bold">No guild members yet</p>
                      <p className="text-sm">Click "RECRUIT" to add your first member</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </div>

          {/* Member Detail Panel */}
          <div>
            <Card className="border overflow-hidden sticky top-4" style={{ background: colors.card, borderColor: colors.border }}>
              <div 
                className="absolute top-0 left-0 w-full h-[2px]"
                style={{ background: `linear-gradient(90deg, transparent, #9945FF, transparent)` }}
              />

              {memberDetail ? (
                <div className="p-4">
                  {/* Member Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <Avatar className="w-16 h-16 border-2" style={{ borderColor: memberDetail.performance?.tier_color }}>
                      <AvatarFallback 
                        className="text-xl font-bold text-white"
                        style={{ background: `linear-gradient(135deg, ${memberDetail.performance?.tier_color}40, transparent)` }}
                      >
                        {memberDetail.employee_name?.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold" style={{ color: colors.text }}>{memberDetail.employee_name}</h3>
                      <p className="text-sm" style={{ color: colors.textMuted }}>{memberDetail.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <TierBadge tier={memberDetail.performance?.tier} theme={theme} />
                        <span className="text-sm font-mono" style={{ color: colors.textMuted }}>Rank #{memberDetail.rank}</span>
                      </div>
                    </div>
                  </div>

                  {/* Tabs for History */}
                  <Tabs defaultValue="overview" className="space-y-4">
                    <TabsList className="grid grid-cols-4 gap-1 p-1 rounded-xl" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9' }}>
                      <TabsTrigger value="overview" className="text-xs rounded-lg data-[state=active]:text-white" style={{ '--tw-bg-opacity': 1 }}>Overview</TabsTrigger>
                      <TabsTrigger value="shifts" className="text-xs rounded-lg data-[state=active]:text-white">Shifts</TabsTrigger>
                      <TabsTrigger value="battles" className="text-xs rounded-lg data-[state=active]:text-white">Battles</TabsTrigger>
                      <TabsTrigger value="activity" className="text-xs rounded-lg data-[state=active]:text-white">Activity</TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-4">
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl text-center"
                        style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc' }}
                      >
                        <p className="text-5xl font-black" style={{ color: memberDetail.performance?.tier_color }}>
                          {memberDetail.performance?.score}
                        </p>
                        <p className="text-sm" style={{ color: colors.textMuted }}>PERFORMANCE SCORE</p>
                      </motion.div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-xl border" style={{ background: 'rgba(0,255,136,0.1)', borderColor: 'rgba(0,255,136,0.2)' }}>
                          <DollarSign className="w-4 h-4 text-[#00FF88] mb-1" />
                          <p className="text-lg font-bold text-[#00FF88]">{formatCurrency(memberDetail.transactions?.total_deposits || 0)}</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Total Deposits</p>
                        </div>
                        <div className="p-3 rounded-xl border" style={{ background: `${colors.accent}15`, borderColor: `${colors.accent}30` }}>
                          <Clock className="w-4 h-4 mb-1" style={{ color: colors.accent }} />
                          <p className="text-lg font-bold" style={{ color: colors.accent }}>{memberDetail.shifts?.total_hours?.toFixed(1) || 0}h</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Hours Worked</p>
                        </div>
                        <div className="p-3 rounded-xl border" style={{ background: 'rgba(153,69,255,0.1)', borderColor: 'rgba(153,69,255,0.2)' }}>
                          <Activity className="w-4 h-4 text-[#9945FF] mb-1" />
                          <p className="text-lg font-bold text-[#9945FF]">{memberDetail.transactions?.efficiency || 0}/hr</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Efficiency</p>
                        </div>
                        <div className="p-3 rounded-xl border" style={{ background: 'rgba(0,245,255,0.1)', borderColor: 'rgba(0,245,255,0.2)' }}>
                          <Zap className="w-4 h-4 text-[#00F5FF] mb-1" />
                          <p className="text-lg font-bold text-[#00F5FF]">{memberDetail.activity?.response_rate || 0}%</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>Response Rate</p>
                        </div>
                      </div>

                      {/* Recommendations */}
                      {memberDetail.recommendations?.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-bold flex items-center gap-2" style={{ color: colors.textMuted }}>
                            <Star className="w-4 h-4 text-[#FFD700]" /> IMPROVEMENT TIPS
                          </h4>
                          {memberDetail.recommendations.slice(0, 3).map((rec, i) => (
                            <div 
                              key={i}
                              className="p-2 rounded-lg text-sm border"
                              style={{ 
                                background: rec.priority === 'high' ? 'rgba(255,51,102,0.1)' : rec.priority === 'medium' ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.02)',
                                borderColor: rec.priority === 'high' ? 'rgba(255,51,102,0.2)' : rec.priority === 'medium' ? 'rgba(255,215,0,0.2)' : colors.border,
                                color: colors.text
                              }}
                            >
                              {rec.message}
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Shifts History Tab */}
                    <TabsContent value="shifts">
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <ScrollArea className="h-[350px]">
                          <div className="space-y-2">
                            {memberDetail.full_shift_history?.length > 0 ? (
                              memberDetail.full_shift_history.map((shift, i) => (
                                <motion.div
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                  className="p-3 rounded-lg border"
                                  style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderColor: colors.border }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-bold flex items-center gap-2" style={{ color: colors.text }}>
                                        <Clock className="w-4 h-4" style={{ color: colors.accent }} />
                                        {formatDateTime(shift.clock_in)}
                                      </p>
                                      <p className="text-xs" style={{ color: colors.textMuted }}>
                                        {shift.clock_out ? `Out: ${formatDateTime(shift.clock_out)}` : '⏱️ Active'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-mono font-bold" style={{ color: colors.accent }}>
                                        {((new Date(shift.clock_out || new Date()) - new Date(shift.clock_in)) / 3600000).toFixed(1)}h
                                      </p>
                                      <Badge 
                                        className="text-xs"
                                        style={{ 
                                          background: shift.status === 'active' ? 'rgba(0,255,136,0.1)' : 'rgba(255,255,255,0.05)',
                                          color: shift.status === 'active' ? '#00FF88' : colors.textMuted
                                        }}
                                      >
                                        {shift.status}
                                      </Badge>
                                    </div>
                                  </div>
                                </motion.div>
                              ))
                            ) : (
                              <div className="text-center py-8" style={{ color: colors.textMuted }}>
                                <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                <p>No shift history</p>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </motion.div>
                    </TabsContent>

                    {/* Battles (Transactions) History Tab */}
                    <TabsContent value="battles">
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <ScrollArea className="h-[350px]">
                          <div className="space-y-2">
                            {memberDetail.full_transaction_history?.length > 0 ? (
                              memberDetail.full_transaction_history.map((trans, i) => (
                                <motion.div
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.03 }}
                                  className="p-3 rounded-lg border"
                                  style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderColor: colors.border }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-bold" style={{ color: colors.text }}>{trans.player_name}</p>
                                      <p className="text-xs" style={{ color: colors.textMuted }}>
                                        {trans.game_name} • {formatRelativeTime(trans.created_at)}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className={`font-mono font-bold ${trans.transaction_type === 'deposit' ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                                        {trans.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(trans.deposit_amount)}
                                      </p>
                                      <Badge 
                                        className="text-xs"
                                        style={{ 
                                          background: trans.status === 'approved' ? 'rgba(0,255,136,0.1)' : 'rgba(255,215,0,0.1)',
                                          color: trans.status === 'approved' ? '#00FF88' : '#FFD700'
                                        }}
                                      >
                                        {trans.status}
                                      </Badge>
                                    </div>
                                  </div>
                                </motion.div>
                              ))
                            ) : (
                              <div className="text-center py-8" style={{ color: colors.textMuted }}>
                                <Flame className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                <p>No battle history</p>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </motion.div>
                    </TabsContent>

                    {/* Activity Checks Tab */}
                    <TabsContent value="activity">
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                          <div className="p-2 rounded-lg" style={{ background: 'rgba(0,255,136,0.1)' }}>
                            <CheckCircle className="w-4 h-4 mx-auto text-[#00FF88] mb-1" />
                            <p className="text-lg font-bold text-white">{memberDetail.activity?.responded || 0}</p>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Responded</p>
                          </div>
                          <div className="p-2 rounded-lg" style={{ background: 'rgba(255,215,0,0.1)' }}>
                            <Timer className="w-4 h-4 mx-auto text-[#FFD700] mb-1" />
                            <p className="text-lg font-bold text-white">{memberDetail.activity?.pending || 0}</p>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Pending</p>
                          </div>
                          <div className="p-2 rounded-lg" style={{ background: 'rgba(255,51,102,0.1)' }}>
                            <XCircle className="w-4 h-4 mx-auto text-[#FF3366] mb-1" />
                            <p className="text-lg font-bold text-white">{memberDetail.activity?.missed || 0}</p>
                            <p className="text-xs" style={{ color: colors.textMuted }}>Missed</p>
                          </div>
                        </div>
                        
                        <ScrollArea className="h-[250px]">
                          <div className="space-y-2">
                            {memberDetail.full_activity_checks?.length > 0 ? (
                              memberDetail.full_activity_checks.map((check, i) => (
                                <motion.div
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                  className="p-2 rounded-lg border flex items-center justify-between"
                                  style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderColor: colors.border }}
                                >
                                  <div className="flex items-center gap-2">
                                    {check.status === 'responded' ? (
                                      <CheckCircle className="w-4 h-4 text-[#00FF88]" />
                                    ) : check.status === 'missed' ? (
                                      <XCircle className="w-4 h-4 text-[#FF3366]" />
                                    ) : (
                                      <Timer className="w-4 h-4 text-[#FFD700]" />
                                    )}
                                    <span className="text-sm" style={{ color: colors.text }}>
                                      {formatRelativeTime(check.sent_at)}
                                    </span>
                                  </div>
                                  {check.response_time_seconds && (
                                    <span className="text-xs font-mono" style={{ color: colors.accent }}>
                                      {check.response_time_seconds.toFixed(1)}s
                                    </span>
                                  )}
                                </motion.div>
                              ))
                            ) : (
                              <div className="text-center py-8" style={{ color: colors.textMuted }}>
                                <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                <p>No activity checks</p>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </motion.div>
                    </TabsContent>
                  </Tabs>
                </div>
              ) : loadingDetail ? (
                <div className="p-8 text-center">
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="mx-auto w-10 h-10 rounded-full border-2" style={{ borderColor: `${colors.accent}30`, borderTopColor: colors.accent }} />
                  <p className="mt-4" style={{ color: colors.textMuted }}>Loading intel...</p>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <motion.div 
                    animate={theme === 'dark' ? { 
                      boxShadow: ['0 0 30px rgba(255,107,0,0.2)', '0 0 60px rgba(255,107,0,0.4)', '0 0 30px rgba(255,107,0,0.2)'],
                    } : {}}
                    transition={{ repeat: Infinity, duration: 3 }}
                    className="w-20 h-20 rounded-2xl border flex items-center justify-center mx-auto mb-4"
                    style={{ 
                      background: theme === 'dark' ? 'linear-gradient(135deg, rgba(255,107,0,0.1), rgba(153,69,255,0.1))' : 'linear-gradient(135deg, rgba(234,88,12,0.05), rgba(139,92,246,0.05))',
                      borderColor: theme === 'dark' ? 'rgba(255,107,0,0.2)' : 'rgba(234,88,12,0.1)'
                    }}
                  >
                    <Eye className="w-10 h-10" style={{ color: colors.accent }} />
                  </motion.div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>SELECT MEMBER</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Click on a guild member to view their history</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* Add Employee Modal */}
      <AnimatePresence>
        {showEmployeeForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: theme === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowEmployeeForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <Card className="p-6 border" style={{ background: colors.card, borderColor: colors.border }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ background: `${colors.accent}20` }}>
                      <UserPlus className="w-5 h-5" style={{ color: colors.accent }} />
                    </div>
                    <h2 className="text-xl font-bold" style={{ color: colors.text }}>RECRUIT MEMBER</h2>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setShowEmployeeForm(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <form onSubmit={handleCreateEmployee} className="space-y-4">
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Full Name</Label>
                    <Input
                      value={employeeForm.username}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, username: e.target.value })}
                      placeholder="John Doe"
                      required
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Email</Label>
                    <Input
                      type="email"
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                      placeholder="john@casino.com"
                      required
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Password</Label>
                    <Input
                      type="password"
                      value={employeeForm.password}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Assign to Arena</Label>
                    <select
                      value={employeeForm.assigned_page_id}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, assigned_page_id: e.target.value })}
                      className="w-full rounded-lg px-3 py-2 border"
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border, color: colors.text }}
                    >
                      <option value="">No arena assigned</option>
                      {branchPages.map((page) => (
                        <option key={page.id} value={page.id}>{page.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 font-bold"
                      style={{ background: `linear-gradient(135deg, ${colors.accent}, #FFD700)`, color: '#000' }}
                    >
                      <Check className="w-4 h-4 mr-2" /> RECRUIT
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowEmployeeForm(false)} className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Employee Modal */}
      <AnimatePresence>
        {editingEmployee && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            onClick={() => setEditingEmployee(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="p-6 border" style={{ background: colors.card, borderColor: colors.border }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl" style={{ background: `${colors.accent}20` }}>
                    <Edit2 className="w-6 h-6" style={{ color: colors.accent }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: colors.text }}>EDIT MEMBER</h2>
                    <p className="text-sm" style={{ color: colors.textMuted }}>Update guild member details</p>
                  </div>
                </div>
                <form onSubmit={handleUpdateEmployee} className="space-y-4">
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Username</Label>
                    <Input
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      placeholder="Username"
                      required
                      style={{ 
                        background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', 
                        borderColor: colors.border, 
                        color: colors.text 
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Email</Label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="Email"
                      required
                      style={{ 
                        background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', 
                        borderColor: colors.border, 
                        color: colors.text 
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>Assign to Page</Label>
                    <select
                      value={editForm.assigned_page_id}
                      onChange={(e) => setEditForm({ ...editForm, assigned_page_id: e.target.value })}
                      className="w-full rounded-lg px-3 py-2 border"
                      style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', borderColor: colors.border, color: colors.text }}
                      data-testid="edit-page-select"
                    >
                      <option value="">No page assigned</option>
                      {branchPages.map((page) => (
                        <option key={page.id} value={page.id}>{page.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label style={{ color: colors.textMuted }}>New Password <span className="text-xs opacity-60">(leave blank to keep current)</span></Label>
                    <Input
                      type="password"
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      placeholder="••••••••"
                      style={{ 
                        background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', 
                        borderColor: colors.border, 
                        color: colors.text 
                      }}
                      data-testid="edit-password-input"
                    />
                  </div>
                  {/* Shift Controls */}
                  <div className="p-3 rounded-lg space-y-2" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${colors.border}` }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" style={{ color: colors.accent }} />
                        <span className="text-sm font-medium" style={{ color: colors.text }}>Shift</span>
                      </div>
                      <Badge className="text-xs" style={{
                        background: editShiftStatus === 'active' ? 'rgba(0,255,136,0.15)' : 'rgba(255,255,255,0.05)',
                        color: editShiftStatus === 'active' ? '#00FF88' : colors.textMuted
                      }}>
                        {editShiftStatus === 'active' ? 'On Shift' : 'Off Shift'}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" disabled={shiftLoading || editShiftStatus === 'active'}
                        onClick={() => handleAdminShift('in')}
                        className="flex-1 h-8 text-xs font-bold"
                        style={{ background: editShiftStatus !== 'active' ? '#00FF88' : 'rgba(255,255,255,0.05)', color: editShiftStatus !== 'active' ? '#000' : colors.textMuted }}
                        data-testid="admin-clock-in"
                      >
                        <Clock className="w-3 h-3 mr-1" /> Clock In
                      </Button>
                      <Button type="button" size="sm" disabled={shiftLoading || editShiftStatus !== 'active'}
                        onClick={() => handleAdminShift('out')}
                        className="flex-1 h-8 text-xs font-bold"
                        style={{ background: editShiftStatus === 'active' ? '#FF3366' : 'rgba(255,255,255,0.05)', color: editShiftStatus === 'active' ? '#fff' : colors.textMuted }}
                        data-testid="admin-clock-out"
                      >
                        <Clock className="w-3 h-3 mr-1" /> Clock Out
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc' }}>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="w-4 h-4 rounded"
                    />
                    <div>
                      <p className="font-medium" style={{ color: colors.text }}>Active Status</p>
                      <p className="text-xs" style={{ color: colors.textMuted }}>Enable/disable member access</p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 font-bold"
                      style={{ background: `linear-gradient(135deg, #00F5FF, #9945FF)`, color: '#000' }}
                    >
                      <Check className="w-4 h-4 mr-2" /> UPDATE
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditingEmployee(null)} className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              <Card className="bg-[#0d0d15] border border-[#FF3366]/30 p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3366]/20 flex items-center justify-center">
                  <Trash2 className="w-8 h-8 text-[#FF3366]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Remove Guild Member?</h3>
                <p className="text-gray-400 text-sm mb-6">This action cannot be undone. The member and their data will be permanently removed from the guild.</p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-white/10"
                    onClick={() => setDeleteConfirm(null)}
                    data-testid="cancel-delete-employee"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-[#FF3366] hover:bg-[#FF3366]/80 text-white"
                    onClick={confirmDeleteEmployee}
                    data-testid="confirm-delete-employee"
                  >
                    Remove
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
